import {useState} from "react";
import styled from "@emotion/styled";
import {Tooltip} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import VehicleIconResolver from "../../vehicles/VehicleIconResolver";

export type DriverMarker = {
  lat: number;
  lng: number;
  driverId: number;
  displayLabel: string;
  vehicleIcon: ReturnType<typeof VehicleIconResolver>;
  color: string;
};

type ClusteredDriverPinsProps = {
  lat: number; // Required by google-map-react for positioning
  lng: number; // Required by google-map-react for positioning
  markers: DriverMarker[];
  newWindow?: Window;
  usePortal?: boolean;
};

const COLLAPSED_OFFSET = 10; // px offset when collapsed
const SPREAD_OFFSET = 36; // px offset when spread (pin width + gap)

export const ClusteredDriverPins = ({
  lat: _lat,
  lng: _lng,
  markers,
  newWindow,
  usePortal
}: ClusteredDriverPinsProps) => {
  const [isSpread, setIsSpread] = useState(false);

  return (
    <ClusterContainer onMouseEnter={() => setIsSpread(true)} onMouseLeave={() => setIsSpread(false)}>
      {markers.map((marker, index) => {
        const offset = isSpread
          ? (index - (markers.length - 1) / 2) * SPREAD_OFFSET
          : (index - (markers.length - 1) / 2) * COLLAPSED_OFFSET;

        return (
          <PinOffset
            key={`driver-cluster-${marker.driverId}-${marker.color}`}
            style={{transform: `translateX(${offset}px)`}}
            isSpread={isSpread}
            zIndex={isSpread ? markers.length - index : index}
          >
            <Tooltip
              content={marker.displayLabel}
              placement="top"
              popoverClassName="marker-popover"
              usePortal={usePortal}
              portalContainer={newWindow?.document.body}
            >
              <DriverPinCircle data-testid="driver-pin-wrapper" data-color={marker.color}>
                <FontAwesomeIcon icon={marker.vehicleIcon} color={marker.color} size="lg" />
              </DriverPinCircle>
            </Tooltip>
          </PinOffset>
        );
      })}
    </ClusterContainer>
  );
};

const ClusterContainer = styled.div`
  position: relative;
  width: 28px;
  height: 28px;
  cursor: pointer;
`;

const PinOffset = styled.div<{isSpread: boolean; zIndex: number}>`
  position: absolute;
  top: 0;
  left: 0;
  transition: transform 0.2s ease;
  z-index: ${({zIndex}) => zIndex};
`;

const DriverPinCircle = styled.div`
  cursor: pointer;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
